import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  Book as BookIcon,
  CheckCircle as CheckIcon,
  Lightbulb as TipIcon,
  BarChart as ChartIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import habitService from '../services/habitService';

const StudyAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = () => {
    const data = habitService.getStudyAnalytics();
    const streakInsights = habitService.getStreakInsights();
    const studyRecommendations = habitService.getStudyRecommendations();
    
    setAnalytics(data);
    setInsights(streakInsights);
    setRecommendations(studyRecommendations);
  };

  const formatTime = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`;
    }
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
  };

  const getSubjectColor = (index) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-purple-500',
      'bg-orange-500',
      'bg-red-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500'
    ];
    return colors[index % colors.length];
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <ChartIcon className="w-4 h-4" /> },
    { id: 'trends', label: 'Trends', icon: <TrendingIcon className="w-4 h-4" /> },
    { id: 'subjects', label: 'Subjects', icon: <BookIcon className="w-4 h-4" /> },
    { id: 'insights', label: 'Insights', icon: <TipIcon className="w-4 h-4" /> }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <ScheduleIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400">Total Sessions</p>
              <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                {analytics?.totalSessions || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-600 dark:text-green-400">Study Days</p>
              <p className="text-2xl font-bold text-green-800 dark:text-green-200">
                {analytics?.studyDaysThisMonth || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <TrendingIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-600 dark:text-purple-400">Total Hours</p>
              <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                {formatTime(analytics?.totalHours || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <CheckIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-orange-600 dark:text-orange-400">Completion</p>
              <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">
                {Math.round(analytics?.completionRate || 0)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
            <TipIcon className="w-5 h-5 text-blue-500" />
            Streak Insights
          </h3>
          <div className="space-y-3">
            {insights.map((insight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg"
              >
                <span className="text-2xl">{insight.icon}</span>
                <p className="text-gray-700 dark:text-gray-300">{insight.message}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
            <TipIcon className="w-5 h-5 text-green-500" />
            Study Recommendations
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg"
              >
                <span className="text-2xl">{rec.icon}</span>
                <p className="text-gray-700 dark:text-gray-300">{rec.message}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderTrends = () => (
    <div className="space-y-6">
      {/* Weekly Trend */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Weekly Study Pattern
        </h3>
        <div className="space-y-4">
          {analytics?.weeklyTrend && Object.entries(analytics.weeklyTrend).map(([day, hours], index) => (
            <div key={day} className="flex items-center gap-4">
              <div className="w-20 text-sm font-medium text-gray-600 dark:text-gray-400">
                {day.slice(0, 3)}
              </div>
              <div className="flex-1">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <motion.div 
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min((hours / 4) * 100, 100)}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  />
                </div>
              </div>
              <div className="w-16 text-sm text-gray-600 dark:text-gray-400 text-right">
                {formatTime(hours)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Session Length Distribution */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Average Session Length
        </h3>
        <div className="text-center">
          <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
            {formatTime(analytics?.averageSessionLength || 0)}
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            per session
          </p>
        </div>
      </div>
    </div>
  );

  const renderSubjects = () => (
    <div className="space-y-6">
      {/* Subject Breakdown */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Subject Breakdown
        </h3>
        <div className="space-y-4">
          {analytics?.subjectBreakdown && Object.entries(analytics.subjectBreakdown).map(([subject, data], index) => (
            <div key={subject} className="flex items-center gap-4">
              <div className={`w-4 h-4 rounded-full ${getSubjectColor(index)}`}></div>
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-800 dark:text-gray-200">{subject}</span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {data.sessions} sessions • {formatTime(data.hours)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <motion.div 
                    className={`${getSubjectColor(index)} h-2 rounded-full`}
                    initial={{ width: 0 }}
                    animate={{ width: `${(data.hours / Math.max(...Object.values(analytics.subjectBreakdown).map(d => d.hours))) * 100}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Most Studied Subject */}
      {analytics?.mostStudiedSubject && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
            <BookIcon className="w-5 h-5 text-purple-500" />
            Most Studied Subject
          </h3>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
              {analytics.mostStudiedSubject}
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              Your favorite subject this month
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderInsights = () => (
    <div className="space-y-6">
      {/* Performance Insights */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Performance Insights
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <span className="text-gray-700 dark:text-gray-300">Study Consistency</span>
            <span className={`font-semibold ${
              analytics?.studyDaysThisMonth >= 20 ? 'text-green-600 dark:text-green-400' :
              analytics?.studyDaysThisMonth >= 15 ? 'text-yellow-600 dark:text-yellow-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {analytics?.studyDaysThisMonth >= 20 ? 'Excellent' :
               analytics?.studyDaysThisMonth >= 15 ? 'Good' : 'Needs Improvement'}
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <span className="text-gray-700 dark:text-gray-300">Session Completion</span>
            <span className={`font-semibold ${
              analytics?.completionRate >= 80 ? 'text-green-600 dark:text-green-400' :
              analytics?.completionRate >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {Math.round(analytics?.completionRate || 0)}%
            </span>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <span className="text-gray-700 dark:text-gray-300">Subject Variety</span>
            <span className={`font-semibold ${
              Object.keys(analytics?.subjectBreakdown || {}).length >= 3 ? 'text-green-600 dark:text-green-400' :
              Object.keys(analytics?.subjectBreakdown || {}).length >= 2 ? 'text-yellow-600 dark:text-yellow-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {Object.keys(analytics?.subjectBreakdown || {}).length} subjects
            </span>
          </div>
        </div>
      </div>

      {/* Tips for Improvement */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
          <TipIcon className="w-5 h-5 text-blue-500" />
          Tips for Improvement
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <p className="text-gray-700 dark:text-gray-300">
              Study at the same time each day to build a consistent routine
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <p className="text-gray-700 dark:text-gray-300">
              Take short breaks every 25-30 minutes to maintain focus
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <p className="text-gray-700 dark:text-gray-300">
              Review your notes within 24 hours for better retention
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <p className="text-gray-700 dark:text-gray-300">
              Mix different subjects to keep your study sessions interesting
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'trends':
        return renderTrends();
      case 'subjects':
        return renderSubjects();
      case 'insights':
        return renderInsights();
      default:
        return null;
    }
  };

  if (!analytics) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center">
          <ChartIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No study data available yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <ChartIcon className="w-6 h-6" />
              Study Analytics
            </h3>
            <p className="text-indigo-100 mt-1">
              Last 30 days performance
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex space-x-1 p-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-indigo-500 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderTabContent()}
        </motion.div>
      </div>
    </div>
  );
};

export default StudyAnalytics; 